import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { AttendanceZoneListResponse, AttendanceZoneOption, extractAttendanceZones } from '@shared/utils/attendance-config.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { OrgUnitPickerDialogComponent, OrgUnitPickerNode } from '../../organization/employees/org-unit-picker-dialog.component';
import { WorkCenterZonePickerDialogComponent } from './work-center-zone-picker-dialog.component';
import {
  WorkCenterConfig,
  WorkCenterCreatePayload,
  WorkCenterFormValue,
  WorkCenterUpdatePayload,
} from './work-center.interface';

type WorkCenterMode = 'view' | 'create' | 'edit';
type WorkCenterScope = 'companiesRecord' | 'orgUnit' | 'employee';
type ConfigKey = keyof WorkCenterFormValue['config'];

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  name: string;
  type?: string;
  parentId?: string | null;
  level?: number | null;
  isActive?: boolean;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

@Component({
  selector: 'app-work-center-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  templateUrl: './work-center-detail.component.html',
  styleUrl: './work-center-detail.component.scss',
})
export class WorkCenterDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: WorkCenterMode = 'view';
  selectedWorkCenterId: string | null = null;
  selectedWorkCenter: WorkCenterConfig | null = null;
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  orgUnitTree: OrgUnitPickerNode[] = [];
  employees: EmployeeRecord[] = [];
  zones: AttendanceZoneOption[] = [];
  selectedCompanyId = '';
  form: WorkCenterFormValue = this.createEmptyForm();

  readonly readAccess = { resource: 'workCenters', action: 'read' } as const;
  readonly createAccess = { resource: 'workCenters', action: 'create' } as const;
  readonly updateAccess = { resource: 'workCenters', action: 'update' } as const;
  readonly deleteAccess = { resource: 'workCenters', action: 'delete' } as const;
  readonly scopeOptions: WorkCenterScope[] = ['companiesRecord', 'orgUnit', 'employee'];
  readonly configOptions: Array<{ key: ConfigKey; titleKey: string; hintKey: string }> = [
    { key: 'requireStartGeo', titleKey: 'workCentersCatalog.config.requireStartGeo', hintKey: 'workCentersCatalog.configHints.requireStartGeo' },
    { key: 'requireEndGeo', titleKey: 'workCentersCatalog.config.requireEndGeo', hintKey: 'workCentersCatalog.configHints.requireEndGeo' },
    { key: 'requireStartEvidence', titleKey: 'workCentersCatalog.config.requireStartEvidence', hintKey: 'workCentersCatalog.configHints.requireStartEvidence' },
    { key: 'requireEndEvidence', titleKey: 'workCentersCatalog.config.requireEndEvidence', hintKey: 'workCentersCatalog.configHints.requireEndEvidence' },
    { key: 'validateStartZone', titleKey: 'workCentersCatalog.config.validateStartZone', hintKey: 'workCentersCatalog.configHints.validateStartZone' },
    { key: 'validateEndZone', titleKey: 'workCentersCatalog.config.validateEndZone', hintKey: 'workCentersCatalog.configHints.validateEndZone' },
    { key: 'allowOutOfZoneStart', titleKey: 'workCentersCatalog.config.allowOutOfZoneStart', hintKey: 'workCentersCatalog.configHints.allowOutOfZoneStart' },
    { key: 'allowOutOfZoneEnd', titleKey: 'workCentersCatalog.config.allowOutOfZoneEnd', hintKey: 'workCentersCatalog.configHints.allowOutOfZoneEnd' },
  ];

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selectedWorkCenter) {
        this.selectedWorkCenter = this.normalizeWorkCenter(this.selectedWorkCenter);
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.loadCompanies().then(() => this.syncWithRoute());
  }

  get hasMultipleCompanies(): boolean {
    return this.companies.length > 1;
  }

  get canCreate(): boolean {
    return this.hasAccess('create');
  }

  get canUpdate(): boolean {
    return this.hasAccess('update');
  }

  get canDelete(): boolean {
    return this.hasAccess('delete');
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('workCentersCatalog.detail.createTitle');
    }
    if (this.isEditMode) {
      return this.t('workCentersCatalog.detail.editTitle');
    }
    return this.selectedWorkCenter?.name || this.t('workCentersCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('workCentersCatalog.detail.createDescription');
    }
    if (this.isEditMode) {
      return this.t('workCentersCatalog.detail.editDescription');
    }
    return this.t('workCentersCatalog.detail.description');
  }

  get availableOrgUnits(): OrgUnitRecord[] {
    return this.orgUnits.filter((item) => item.companiesRecordId === this.form.companiesRecordId);
  }

  get canSelectOrgUnit(): boolean {
    return !!this.form.companiesRecordId && this.orgUnitTree.length > 0;
  }

  get availableEmployees(): EmployeeRecord[] {
    return this.employees.filter((item) => item.companiesRecordId === this.form.companiesRecordId);
  }

  get selectedEmployeeLabel(): string {
    const employee = this.availableEmployees.find((item) => item.employeeId === this.form.employeeId);
    if (!employee) {
      return this.selectedWorkCenter?.employeeNumber || '-';
    }
    return `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`;
  }

  get selectedOrgUnitName(): string {
    return this.availableOrgUnits.find((item) => item.orgUnitId === this.form.orgUnitId)?.name || this.form.orgUnitId || '-';
  }

  get selectedOrgUnitPath(): string {
    const node = this.findOrgUnitNodeById(this.orgUnitTree, this.form.orgUnitId);
    return node?.pathLabels.join(' / ') || this.selectedOrgUnitName;
  }

  async onCompanyChange(): Promise<void> {
    this.selectedCompanyId = this.form.companiesRecordId;
    this.form.orgUnitId = '';
    this.form.employeeId = '';
    this.form.allowedZoneIds = [];
    await this.loadDependentLookups(this.form.companiesRecordId);
  }

  onScopeLevelChange(): void {
    if (this.form.scopeLevel === 'companiesRecord') {
      this.form.orgUnitId = '';
      this.form.employeeId = '';
      return;
    }
    if (this.form.scopeLevel === 'orgUnit') {
      this.form.employeeId = '';
      return;
    }
    this.form.orgUnitId = this.resolveSelectedEmployeeOrgUnitId(this.form.employeeId) || '';
  }

  onEmployeeChange(employeeId: string): void {
    this.form.employeeId = employeeId;
    this.form.orgUnitId = this.resolveSelectedEmployeeOrgUnitId(employeeId) || '';
  }

  openOrgUnitPicker(): void {
    if (this.isViewMode || !this.canSelectOrgUnit) {
      return;
    }

    const dialogRef = this.dialog.open(OrgUnitPickerDialogComponent, {
      width: '920px',
      maxWidth: '95vw',
      data: {
        nodes: this.orgUnitTree,
        selectedOrgUnitId: this.form.orgUnitId,
        allowAnyLevel: true,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((selectedNode: OrgUnitPickerNode | null | undefined) => {
      if (selectedNode === undefined) {
        return;
      }

      this.form.orgUnitId = selectedNode?.orgUnitId || '';
    });
  }

  openZonePicker(): void {
    if (this.isSaving || !this.form.companiesRecordId) {
      return;
    }

    const dialogRef = this.dialog.open(WorkCenterZonePickerDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        zones: this.zones,
        selectedZoneIds: this.form.allowedZoneIds,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((selectedZoneIds: string[] | undefined) => {
      if (!selectedZoneIds) {
        return;
      }

      this.form.allowedZoneIds = selectedZoneIds;
    });
  }

  startCreate(): void {
    if (!this.canCreate) {
      return;
    }
    const queryParams = this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined;
    void this.router.navigate(['/attendance/work-centers/new'], { queryParams });
  }

  startEdit(): void {
    if (!this.selectedWorkCenterId || !this.canUpdate) {
      return;
    }
    void this.router.navigate(['/attendance/work-centers', this.selectedWorkCenterId, 'edit']);
  }

  cancelEdit(): void {
    if (this.selectedWorkCenterId) {
      void this.router.navigate(['/attendance/work-centers', this.selectedWorkCenterId]);
      return;
    }
    void this.router.navigate(['/attendance/work-centers']);
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/work-centers']);
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const created = await firstValueFrom(this.apiService.post<WorkCenterConfig>('work-centers', this.buildCreatePayload()));
        this.showNotification('snackbar-success', this.t('workCentersCatalog.notifications.created'), 'bottom', 'center');
        void this.router.navigate(['/attendance/work-centers', created.workCenterId]);
      } else if (this.isEditMode && this.selectedWorkCenterId) {
        await firstValueFrom(
          this.apiService.patch<WorkCenterConfig>(`work-centers/${this.selectedWorkCenterId}`, this.buildUpdatePayload())
        );
        this.showNotification('snackbar-success', this.t('workCentersCatalog.notifications.updated'), 'bottom', 'center');
        void this.router.navigate(['/attendance/work-centers', this.selectedWorkCenterId]);
      }
    } catch (error) {
      console.error('Error saving work center', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('workCentersCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedWorkCenter || !this.selectedWorkCenterId || !this.canDelete) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('workCentersCatalog.dialog.deleteTitle'),
        message: this.t('workCentersCatalog.dialog.deleteMessage', { workCenter: this.selectedWorkCenter.name }),
        confirmLabel: this.t('workCentersCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedWorkCenterId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(this.apiService.delete(`work-centers/${this.selectedWorkCenterId}`));
        this.showNotification('snackbar-success', this.t('workCentersCatalog.notifications.deleted'), 'bottom', 'center');
        void this.router.navigate(['/attendance/work-centers']);
      } catch (error) {
        console.error('Error deleting work center', error);
        this.showNotification(
          'snackbar-danger',
          this.getApiErrorMessage(error, this.t('workCentersCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  getScopeLevelLabel(scopeLevel: string | null | undefined): string {
    const normalized = this.normalizeText(scopeLevel) || 'orgUnit';
    if (this.scopeOptions.includes(normalized as WorkCenterScope)) {
      return this.t(`workCentersCatalog.scopeOptions.${normalized}`);
    }
    return normalized;
  }

  getBooleanLabel(value: boolean): string {
    return value ? this.t('common.yes') : this.t('common.no');
  }

  getCompanyName(companyId: string | null | undefined): string {
    if (!companyId) {
      return this.t('workCentersCatalog.labels.noCompany');
    }
    return this.companies.find((item) => item.companiesRecordId === companyId)?.name || this.t('workCentersCatalog.labels.noCompany');
  }

  getZoneLabels(zoneIds: string[] | null | undefined): string {
    if (!zoneIds?.length) {
      return this.t('workCentersCatalog.labels.noZones');
    }
    return zoneIds
      .map((zoneId) => this.zones.find((zone) => zone.zoneId === zoneId)?.name || zoneId)
      .join(', ');
  }

  private async syncWithRoute(): Promise<void> {
    const workCenterId = this.route.snapshot.paramMap.get('workCenterId');
    const companyId = this.route.snapshot.queryParamMap.get('companyId') || '';
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/work-centers/new');
    const isEditRoute = /\/attendance\/work-centers\/[^/]+\/edit$/.test(currentUrl);

    if (isCreateRoute) {
      this.mode = 'create';
      this.selectedWorkCenterId = null;
      this.selectedWorkCenter = null;
      this.selectedCompanyId = companyId || this.getDefaultCompanyId();
      this.form = this.createEmptyForm(this.selectedCompanyId);
      await this.loadDependentLookups(this.selectedCompanyId);
      this.isLoading = false;
      return;
    }

    if (!workCenterId) {
      this.mode = 'view';
      this.selectedWorkCenterId = null;
      this.selectedWorkCenter = null;
      this.isLoading = false;
      return;
    }

    await this.loadWorkCenter(workCenterId);
    this.mode = isEditRoute ? 'edit' : 'view';
  }

  private async loadWorkCenter(workCenterId: string): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(this.apiService.get<WorkCenterConfig>(`work-centers/${workCenterId}`));
      const normalized = this.normalizeWorkCenter(response);
      this.selectedWorkCenterId = normalized.workCenterId;
      this.selectedWorkCenter = normalized;
      this.selectedCompanyId = normalized.companiesRecordId;
      await this.loadDependentLookups(normalized.companiesRecordId);
      this.form = this.createFormFromWorkCenter(normalized);
      this.ensureSelectedEmployeeOption(normalized);
    } catch (error) {
      console.error('Error loading work center detail', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('workCentersCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.extractCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading companies for work centers', error);
      this.companies = [];
    }
  }

  private async loadDependentLookups(companyId: string): Promise<void> {
    if (!companyId) {
      this.orgUnits = [];
      this.orgUnitTree = [];
      this.employees = [];
      this.zones = [];
      return;
    }

    await Promise.all([
      this.loadOrgUnits(companyId),
      this.loadEmployees(companyId),
      this.loadZones(companyId),
    ]);
  }

  private async loadOrgUnits(companyId: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      this.orgUnits = this.extractOrgUnits(response)
        .filter((item) => item.companiesRecordId === companyId && item.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.orgUnitTree = this.buildOrgUnitTree(this.orgUnits);
    } catch (error) {
      console.error('Error loading org units for work centers', error);
      this.orgUnits = [];
      this.orgUnitTree = [];
    }
  }

  private async loadEmployees(companyId: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: { companiesRecordId: companyId, page: 1, perPage: 200 },
        })
      );
      this.employees = this.extractEmployees(response)
        .filter((item) => item.companiesRecordId === companyId)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));
    } catch (error) {
      console.error('Error loading employees for work centers', error);
      this.employees = [];
    }
  }

  private async loadZones(companyId: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceZoneListResponse>('attendance/zones', {
          params: { companiesRecordId: companyId, isActive: 'true', page: 1, perPage: 200 },
        })
      );
      this.zones = extractAttendanceZones(response)
        .filter((zone) => zone.companiesRecordId === companyId)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading zones for work centers', error);
      this.zones = [];
    }
  }

  private extractCompanies(response: CompaniesRecord[] | CompaniesResponse): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private extractOrgUnits(response: OrgUnitRecord[] | OrgUnitsResponse): OrgUnitRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.orgUnits || [];
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private createEmptyForm(companyId = this.getDefaultCompanyId()): WorkCenterFormValue {
    return {
      companiesRecordId: companyId,
      name: '',
      description: '',
      address: '',
      reference: '',
      scopeLevel: 'companiesRecord',
      orgUnitId: '',
      employeeId: '',
      allowedZoneIds: [],
      isActive: true,
      config: {
        requireStartGeo: true,
        requireEndGeo: true,
        requireStartEvidence: false,
        requireEndEvidence: false,
        validateStartZone: true,
        validateEndZone: true,
        allowOutOfZoneStart: false,
        allowOutOfZoneEnd: false,
      },
    };
  }

  private createFormFromWorkCenter(item: WorkCenterConfig): WorkCenterFormValue {
    return {
      companiesRecordId: item.companiesRecordId,
      name: item.name,
      description: item.description || '',
      address: item.address || '',
      reference: item.reference || '',
      scopeLevel: (this.normalizeText(item.scopeLevel) || 'orgUnit'),
      orgUnitId: item.orgUnitId || '',
      employeeId: item.employeeId || '',
      allowedZoneIds: [...item.allowedZoneIds],
      isActive: item.isActive !== false,
      config: {
        requireStartGeo: item.config.requireStartGeo === true,
        requireEndGeo: item.config.requireEndGeo === true,
        requireStartEvidence: item.config.requireStartEvidence === true,
        requireEndEvidence: item.config.requireEndEvidence === true,
        validateStartZone: item.config.validateStartZone === true,
        validateEndZone: item.config.validateEndZone === true,
        allowOutOfZoneStart: item.config.allowOutOfZoneStart === true,
        allowOutOfZoneEnd: item.config.allowOutOfZoneEnd === true,
      },
    };
  }

  private validateForm(): string | null {
    if (!this.form.companiesRecordId) {
      return this.t('workCentersCatalog.notifications.validationCompany');
    }
    if (!this.normalizeText(this.form.name)) {
      return this.t('workCentersCatalog.notifications.validationName');
    }
    if (!this.normalizeText(this.form.scopeLevel)) {
      return this.t('workCentersCatalog.notifications.validationScope');
    }
    if (this.form.scopeLevel === 'orgUnit' && !this.form.orgUnitId) {
      return this.t('workCentersCatalog.notifications.validationOrgUnit');
    }
    if (this.form.scopeLevel === 'employee' && !this.form.employeeId) {
      return this.t('workCentersCatalog.notifications.validationEmployee');
    }
    return null;
  }

  private buildCreatePayload(): WorkCenterCreatePayload {
    const employeeOrgUnitId = this.resolveSelectedEmployeeOrgUnitId(this.form.employeeId);
    return {
      companiesRecordId: this.form.companiesRecordId,
      name: this.normalizeText(this.form.name),
      description: this.normalizeOptionalText(this.form.description) || null,
      address: this.normalizeOptionalText(this.form.address) || null,
      reference: this.normalizeOptionalText(this.form.reference) || null,
      scopeLevel: this.form.scopeLevel,
      orgUnitId: this.form.scopeLevel === 'companiesRecord'
        ? null
        : this.form.scopeLevel === 'orgUnit'
          ? this.form.orgUnitId
          : (employeeOrgUnitId || this.form.orgUnitId || null),
      employeeId: this.form.scopeLevel === 'employee' ? this.form.employeeId : null,
      config: { ...this.form.config },
      allowedZoneIds: [...this.form.allowedZoneIds],
    };
  }

  private buildUpdatePayload(): WorkCenterUpdatePayload {
    return {
      ...this.buildCreatePayload(),
      isActive: this.form.isActive,
    };
  }

  private normalizeWorkCenter(item: Partial<WorkCenterConfig>): WorkCenterConfig {
    const scopeLevel = this.normalizeText(item.scopeLevel) || 'orgUnit';
    const allowedZoneIds = Array.isArray(item.allowedZoneIds) ? item.allowedZoneIds.filter(Boolean) : [];
    const config = {
      requireStartGeo: item.config?.requireStartGeo === true,
      requireEndGeo: item.config?.requireEndGeo === true,
      requireStartEvidence: item.config?.requireStartEvidence === true,
      requireEndEvidence: item.config?.requireEndEvidence === true,
      validateStartZone: item.config?.validateStartZone === true,
      validateEndZone: item.config?.validateEndZone === true,
      allowOutOfZoneStart: item.config?.allowOutOfZoneStart === true,
      allowOutOfZoneEnd: item.config?.allowOutOfZoneEnd === true,
    };

    return {
      workCenterId: item.workCenterId || crypto.randomUUID(),
      center: item.center || '',
      companiesRecordId: item.companiesRecordId || '',
      name: item.name || '',
      description: item.description || '',
      address: item.address || '',
      reference: item.reference || '',
      scopeLevel,
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      employeeId: item.employeeId || null,
      employeeNumber: item.employeeNumber || null,
      config,
      allowedZoneIds,
      isActive: item.isActive !== false,
      companyName: this.getCompanyName(item.companiesRecordId),
      scopeLevelLabel: this.getScopeLevelLabel(scopeLevel),
      scopeTargetLabel: this.getScopeTargetLabel(scopeLevel, item.orgUnitId, item.employeeId, item.employeeNumber),
      zonesSummaryLabel: this.t('workCentersCatalog.labels.zonesCount', { count: allowedZoneIds.length }),
      activeLabel: item.isActive !== false ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private getScopeTargetLabel(
    scopeLevel: string | null | undefined,
    orgUnitId: string | null | undefined,
    employeeId: string | null | undefined,
    employeeNumber: string | null | undefined
  ): string {
    if (scopeLevel === 'companiesRecord') {
      return this.getCompanyName(this.selectedWorkCenter?.companiesRecordId || this.form.companiesRecordId || null);
    }
    if (scopeLevel === 'employee' && employeeId) {
      const employee = this.availableEmployees.find((item) => item.employeeId === employeeId);
      return employee ? `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}` : employeeNumber || employeeId;
    }
    if (scopeLevel === 'orgUnit' && orgUnitId) {
      return this.availableOrgUnits.find((item) => item.orgUnitId === orgUnitId)?.name || orgUnitId;
    }
    return '-';
  }

  private resolveSelectedEmployeeOrgUnitId(employeeId: string | null | undefined): string | null {
    if (!employeeId) {
      return null;
    }
    return this.availableEmployees.find((item) => item.employeeId === employeeId)?.orgUnitId || null;
  }

  private buildOrgUnitTree(orgUnits: OrgUnitRecord[]): OrgUnitPickerNode[] {
    const nodeMap = new Map<string, OrgUnitPickerNode>();
    const roots: OrgUnitPickerNode[] = [];

    const sortedUnits = [...orgUnits].sort((left, right) => {
      const levelDiff = (left.level || 0) - (right.level || 0);
      return levelDiff !== 0 ? levelDiff : left.name.localeCompare(right.name);
    });

    for (const orgUnit of sortedUnits) {
      nodeMap.set(orgUnit.orgUnitId, {
        orgUnitId: orgUnit.orgUnitId,
        name: orgUnit.name,
        type: orgUnit.type || '',
        level: orgUnit.level || 1,
        children: [],
        isLeaf: true,
        pathLabels: [],
      });
    }

    for (const orgUnit of sortedUnits) {
      const node = nodeMap.get(orgUnit.orgUnitId);
      if (!node) {
        continue;
      }

      const parentId = orgUnit.parentId || '';
      const parentNode = parentId ? nodeMap.get(parentId) : undefined;

      if (parentNode) {
        parentNode.children.push(node);
        parentNode.isLeaf = false;
      } else {
        roots.push(node);
      }
    }

    const assignPaths = (nodes: OrgUnitPickerNode[], parentPath: string[] = []): void => {
      nodes.sort((left, right) => left.name.localeCompare(right.name));
      nodes.forEach((node) => {
        node.pathLabels = [...parentPath, node.name];
        assignPaths(node.children, node.pathLabels);
      });
    };

    assignPaths(roots);
    return roots;
  }

  private findOrgUnitNodeById(nodes: OrgUnitPickerNode[], orgUnitId: string): OrgUnitPickerNode | null {
    for (const node of nodes) {
      if (node.orgUnitId === orgUnitId) {
        return node;
      }

      const childMatch = this.findOrgUnitNodeById(node.children, orgUnitId);
      if (childMatch) {
        return childMatch;
      }
    }

    return null;
  }

  private ensureSelectedEmployeeOption(item: WorkCenterConfig): void {
    if (!item.employeeId || this.employees.some((employee) => employee.employeeId === item.employeeId)) {
      return;
    }
    this.employees = [
      ...this.employees,
      {
        employeeId: item.employeeId,
        center: item.center,
        employeeNumber: item.employeeNumber || item.employeeId,
        firstName: '',
        lastName: '',
        secondLastName: '',
        fullName: item.employeeNumber || item.employeeId,
        companiesRecordId: item.companiesRecordId,
        orgUnitId: item.orgUnitId || '',
        status: item.isActive ? 'active' : 'inactive',
        isActive: item.isActive,
      },
    ];
  }

  private getDefaultCompanyId(): string {
    return this.companies.length === 1 ? this.companies[0].companiesRecordId : this.selectedCompanyId || '';
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    const normalized = this.normalizeText(value);
    return normalized || undefined;
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    return extractApiErrorMessage(error, fallback);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'workCenters', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
